import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchMeWithRetry(retries = 1, delayMs = 400) {
  let lastErr;
  let down = 0;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Server restarting (deploy): 5xx or no answer. Keep the session and wait, up to about 90 seconds.
      if ((!status || status >= 500) && down < 45) { down += 1; attempt -= 1; await wait(2000); continue; }
      if (status && status !== 401) throw err;
      if (attempt < retries) await wait(delayMs);
    }
  }
  throw lastErr;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const data = await fetchMeWithRetry(1, 500);
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback in progress, skip - AuthCallback will drive checkAuth after session exchange.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { void e; }
    setUser(null);
    window.location.href = "/";
  }, []);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
